/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { memo } from 'react';
import { FormattedMessage, type IntlShape } from 'react-intl';
import {
  Avatar,
  Box,
  Icon,
  InlineMessage,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_STL, CF_SEC_BLD, l } from '../../../../../shared/constants';
import { getStatusKind } from '../../../utils';

import './SidePanelOwnerItem.css';

interface SidePanelOwnerItemProps {
  intl: IntlShape;
  ownerName: string | null;
  ownerRank: number | null;
  ownerStatus: string | null;
}

/**
 * Displays a single owner row inside the side panel, showing an avatar
 * with initials, name, rank, status indicator, and an inline warning
 * when the owner is inactive.
 * @param props - {@link SidePanelOwnerItemProps}
 * @param props.ownerName - Display name of the owner.
 * @param props.ownerRank - Numeric rank of the owner.
 * @param props.ownerStatus - Status string (e.g. "active" or "inactive").
 * @returns The rendered owner row with optional inactive warning.
 */
function SidePanelOwnerItem({
  intl,
  ownerName,
  ownerRank,
  ownerStatus,
}: SidePanelOwnerItemProps) {
  const userName =
    ownerName?.split(' ').join('.').split('.').filter(Boolean) ?? '';
  const userInitials =
    userName.length >= 2
      ? userName[0].charAt(0).toUpperCase() +
        userName[userName.length - 1].charAt(0).toUpperCase()
      : 'NA';
  const isInactive = ownerStatus?.toLowerCase() === 'inactive' || false;
  const { statusColor, statusText, statusTextKey } = getStatusKind(
    ownerStatus ?? ''
  );

  return (
    <Box tag='div' className='SidePanelOwnerItem-wrapper'>
      <Box tag='div' className='SidePanelOwnerItem'>
        <Avatar size='small'>{userInitials}</Avatar>
        <Box tag='div' className='SidePanelOwnerItem-detailsWrapper'>
          <Typography
            kind='body2'
            color={isInactive ? CF_NEU_STL : CF_SEC_BLD}
            lineLimit={1}>
            {ownerName}
          </Typography>
          <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
            <FormattedMessage
              id='FbWEGs'
              defaultMessage='Rank {ownerRank}'
              description='Displayed as "Rank {ownerRank}" where {ownerRank} is a number representing the rank of the owner.'
              values={{ ownerRank }}
            />
          </Typography>
        </Box>
        {statusText.trim().length > 0 && (
          <Typography
            kind='body3'
            color={statusColor}
            className='SidePanelOwnerItem-status'>
            {`● ${intl.formatMessage(l[statusTextKey])}`}
          </Typography>
        )}
      </Box>
      {isInactive && (
        <Box tag='div' className='SidePanelOwnerItem-inactiveMsgWrapper'>
          <InlineMessage
            text={intl.formatMessage(
              {
                id: 'ZRZZit',
                defaultMessage:
                  '{ownerName} is no longer with the company. Please remove and set a new Primary Owner.',
                description: 'Owner is not associated with company',
              },
              { ownerName }
            )}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        </Box>
      )}
    </Box>
  );
}

export default memo(SidePanelOwnerItem);
